'use client';

import {
    MotionMaybe,
    normalizeAnimationPreset,
    useMotionControls,
} from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { MarkDown } from './atoms/MarkDown';

const SOCIAL_ICON_MAP = {
    linkedin: 'fa-brands fa-linkedin-in',
    facebook: 'fa-brands fa-facebook',
    instagram: 'fa-brands fa-instagram',
    twitter: 'fa-brands fa-x-twitter',
    x: 'fa-brands fa-x-twitter',
    google: 'fa-brands fa-google',
    phone: 'fa-solid fa-phone',
    tel: 'fa-solid fa-phone',
    yelp: 'fa-brands fa-yelp',
    pinterest: 'fa-brands fa-pinterest',
    youtube: 'fa-brands fa-youtube',
    houzz: 'fa-brands fa-houzz',
    mobilo: 'fa-kit fa-mobilo',
    whatsapp: 'fa-brands fa-whatsapp',
    sms: 'fa-solid fa-message-sms',
    tiktok: 'fa-brands fa-tiktok',
    flickr: 'fa-brands fa-flickr',
    email: 'fa-solid fa-envelope',
    mail: 'fa-solid fa-envelope',
    angi: 'fa-kit fa-angi',
    angislist: 'fa-kit fa-angi',
    angieslist: 'fa-kit fa-angi',
    bbb: 'fa-kit fa-bbb',
    betterbusinessbureau: 'fa-kit fa-bbb',
    capterra: 'fa-kit fa-capterra',
    homestars: 'fa-kit fa-homestars',
    nextdoor: 'fa-kit fa-nextdoor',
    nicejob: 'fa-kit fa-nicejob',
    thumbtack: 'fa-kit fa-thumbtack',
    yellowpages: 'fa-kit fa-yellowpages',
    yp: 'fa-kit fa-yellowpages',
};

const SOCIAL_LINK_KEYS = ['Link', 'URL', 'Url', 'Href', 'link', 'href'];

function normalizeSocialKey(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

function getSocialName(item) {
    if (!item || typeof item !== 'object') return '';
    return item.Name || item.Label || item.Title || item.name || '';
}

function getSocialLink(item) {
    if (!item || typeof item !== 'object') return '';
    for (const key of SOCIAL_LINK_KEYS) {
        if (item[key]) return item[key];
    }
    return '';
}

function normalizeSocialHref(nameKey, raw) {
    const url = String(raw || '').trim();
    if (!url) return '';
    if ((nameKey === 'email' || nameKey === 'mail') && !/^mailto:/i.test(url)) {
        if (url.includes('@')) return `mailto:${url}`;
    }
    if ((nameKey === 'phone' || nameKey === 'tel') && !/^tel:/i.test(url)) {
        return `tel:${url}`;
    }
    if (nameKey === 'sms' && !/^sms:/i.test(url)) {
        return `sms:${url}`;
    }
    return url;
}

function buildSocialLinks(rawSocials) {
    const list = Array.isArray(rawSocials) ? rawSocials : [];
    const seen = new Set();
    return list
        .map((item) => {
            const name = String(getSocialName(item)).trim();
            const nameKey = normalizeSocialKey(name);
            const iconClass = SOCIAL_ICON_MAP[nameKey];
            if (!iconClass) return null;
            const href = normalizeSocialHref(nameKey, getSocialLink(item));
            if (!href) return null;
            const key = nameKey || href;
            if (seen.has(key)) return null;
            seen.add(key);
            return { name, href, iconClass };
        })
        .filter(Boolean);
}

export default function Team(props) {
    const {
        Tagline = '',
        Header = '',
        Body = '',
        People = [],
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Alignment = 'center',
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = '',

        EditorPreview = false,
    } = props;

    const renderedPeople =
        Array.isArray(People) && People.length > 0 ? People : [];
    const hasPeople = renderedPeople.length > 0;
    const hasSinglePerson = renderedPeople.length === 1;
    const personHeadingVariant = getChildHeadingVariant(Header);

    const requestedGroup = String(AnimationGroup ?? '')
        .toLowerCase()
        .trim();

    let normalizedGroup = 'content';

    if (requestedGroup === 'ctas') {
        normalizedGroup = 'ctas';
    } else if (requestedGroup === 'features') {
        normalizedGroup = 'features';
    } else if (
        hasPeople &&
        (String(Animation).toLowerCase().trim() === 'stagger' ||
            String(Animation).toLowerCase().trim() === 'cascade')
    ) {
        normalizedGroup = 'features';
    } else if (hasPeople && !requestedGroup) {
        normalizedGroup = 'features';
    }

    let numColumns;
    if (renderedPeople.length === 1) {
        numColumns = 'grid-cols-1';
    } else if (renderedPeople.length === 2) {
        numColumns = 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2';
    } else if (renderedPeople.length === 3) {
        numColumns = 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3';
    } else if (renderedPeople.length === 5) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    } else if (renderedPeople.length === 9) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    } else if (renderedPeople.length === 6) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    } else if (renderedPeople.length === 10) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    } else if (renderedPeople.length === 15) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    } else {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4';
    }
    const sideColumns = numColumns;
    const sideImageClass =
        renderedPeople.length >= 4
            ? 'h-24 w-24 md:h-28 md:w-28 lg:h-32 lg:w-32'
            : 'h-28 w-28 md:h-32 md:w-32 lg:h-36 lg:w-36';

    const vSpace = getVerticalSpacing(VerticalSpacing);

    const {
        backgroundColor,
        textColor,
        headerColor,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);
    const isLeftAligned = Alignment === 'left';
    const isRightAligned = Alignment === 'right';
    const isCentered = Alignment === 'center';
    const containerAlign = isCentered ? 'mx-auto' : '';
    const textAlign = isCentered ? 'text-center' : 'text-left';

    const durationValue = Number(Duration) || 0.6;
    const normalizedPreset = normalizeAnimationPreset(
        Animation,
        normalizedGroup
    );
    const { enableChild, childMotionProps, containerMotionProps, remountKey } =
        useMotionControls({
            preset: normalizedPreset,
            duration: durationValue,
            editorPreview: EditorPreview,
            group: normalizedGroup,
            keyBase: 'team',
        });
    const shouldAnimContent = enableChild('content');
    const shouldAnimFeatures = enableChild('features');
    const gridTemplate =
        isRightAligned && !isCentered
            ? 'lg:grid-cols-[1fr_minmax(0,320px)]'
            : 'lg:grid-cols-[minmax(0,320px)_1fr]';
    const renderSocialLinks = (person, alignClassName) => {
        const socialSource = Array.isArray(person?.Socials)
            ? person.Socials
            : Array.isArray(person?.Social)
            ? person.Social
            : [];
        const socials = buildSocialLinks(socialSource);
        if (!socials.length) return null;

        return (
            <div
                className={`mb-2 mt-3 flex flex-wrap items-center gap-3 text-sm leading-none ${alignClassName}`}
            >
                {socials.map((social) => (
                    <a
                        key={`${social.name}-${social.href}`}
                        href={social.href}
                        aria-label={social.name}
                        className={`inline-flex items-center justify-center ${textColor} transition hover:opacity-80`}
                    >
                        <i className={social.iconClass} aria-hidden="true" />
                    </a>
                ))}
            </div>
        );
    };
    const singlePersonDescriptionWidth = hasSinglePerson ? 'max-w-[700px]' : '';

    return (
        <section
            id="team"
            className={`editable-component relative overflow-hidden px-6 lg:px-8 ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
            data-json="team"
        >
            <div className="relative z-40 mx-auto max-w-7xl">
                {isCentered ? (
                    <>
                        <MotionMaybe
                            enable={shouldAnimContent}
                            nodeKey={`${remountKey}-center-text`}
                            motionProps={childMotionProps}
                        >
                            <div
                                className={`${
                                    containerAlign || 'mx-auto'
                                } max-w-2xl ${textAlign} lg:max-w-7xl`}
                            >
                                {Tagline && (
                                    <MarkDown
                                        className={`${
                                            containerAlign || 'mx-auto'
                                        } max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${taglineColor} ${textAlign}`}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                )}
                                {(Header || Body) && (
                                    <div
                                        className={`mb-12 flex w-full max-w-md flex-col md:max-w-2xl lg:mb-16 ${containerAlign} ${textAlign}`}
                                    >
                                        {Header && (
                                            <MarkDown
                                                variant="h2"
                                                className={`z-40 max-w-2xl font-display text-2xl tracking-tight ${headerColor} ${textAlign} md:text-3xl lg:text-4xl`}
                                            >
                                                {Header}
                                            </MarkDown>
                                        )}
                                        {Body && (
                                            <MarkDown
                                                className={`mt-6 max-w-2xl text-lg font-text leading-8 ${textColor} ${textAlign}`}
                                            >
                                                {Body}
                                            </MarkDown>
                                        )}
                                    </div>
                                )}
                            </div>
                        </MotionMaybe>
                        <MotionMaybe
                            enable={shouldAnimFeatures}
                            nodeKey={`${remountKey}-center-grid`}
                            motionProps={containerMotionProps}
                        >
                            <ul
                                className={`mx-auto grid ${numColumns} justify-items-center gap-8 text-center lg:gap-10`}
                            >
                                {renderedPeople.map(({ Person }) => {
                                    const { x: imagePosX, y: imagePosY } =
                                        parseImagePosition(
                                            Person.ImagePosition
                                        );
                                    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;
                                    return (
                                        <MotionMaybe
                                            key={Person.Name}
                                            enable={shouldAnimFeatures}
                                            nodeKey={`${remountKey}-center-person-${Person.Name}`}
                                            motionProps={childMotionProps}
                                            className="text-center"
                                        >
                                            <div className="flex flex-col items-center">
                                                <div className="relative">
                                                    <img
                                                        className="h-24 w-24 rounded-[var(--shape-radius)] object-cover md:h-32 md:w-32 lg:h-44 lg:w-44"
                                                        src={Person.ImageURL}
                                                        alt={Person.Name}
                                                        loading="lazy"
                                                        style={{
                                                            objectPosition:
                                                                imageObjectPosition,
                                                        }}
                                                    />
                                                </div>
                                                <MarkDown
                                                    variant={
                                                        personHeadingVariant
                                                    }
                                                    className={`mt-6 font-display text-sm leading-4 tracking-tight md:text-base ${taglineColor} text-center`}
                                                >
                                                    {Person.Name}
                                                </MarkDown>
                                                <MarkDown
                                                    className={`mt-2 text-xs font-text leading-4 md:text-sm ${textColor} text-center`}
                                                >
                                                    {Person.Role}
                                                </MarkDown>
                                                {renderSocialLinks(
                                                    Person,
                                                    'justify-center'
                                                )}
                                                {Person.Description && (
                                                    <MarkDown
                                                        className={`mt-2 text-xs font-text leading-4 ${textColor} text-center ${singlePersonDescriptionWidth} mx-auto`}
                                                    >
                                                        {Person.Description}
                                                    </MarkDown>
                                                )}
                                            </div>
                                        </MotionMaybe>
                                    );
                                })}
                            </ul>
                        </MotionMaybe>
                    </>
                ) : (
                    <div
                        className={`mx-auto grid w-full max-w-7xl gap-10 ${gridTemplate}`}
                    >
                        <div
                            className={`w-full max-w-2xl ${textAlign} ${
                                isRightAligned ? 'lg:order-2' : ''
                            }`}
                        >
                            <MotionMaybe
                                enable={shouldAnimContent}
                                nodeKey={`${remountKey}-side-text`}
                                motionProps={childMotionProps}
                            >
                                <div className={textAlign}>
                                    {Tagline && (
                                        <MarkDown
                                            className={`max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${taglineColor} ${textAlign}`}
                                        >
                                            {Tagline}
                                        </MarkDown>
                                    )}
                                    {(Header || Body) && (
                                        <div className="mb-12 flex w-full max-w-md flex-col md:max-w-2xl lg:mb-16">
                                            {Header && (
                                                <MarkDown
                                                    variant="h2"
                                                    className={`font-display text-2xl tracking-tight ${headerColor} ${textAlign} md:text-3xl lg:text-4xl`}
                                                >
                                                    {Header}
                                                </MarkDown>
                                            )}
                                            {Body && (
                                                <MarkDown
                                                    className={`mt-6 max-w-2xl text-lg font-text leading-8 ${textColor} ${textAlign}`}
                                                >
                                                    {Body}
                                                </MarkDown>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </MotionMaybe>
                        </div>

                        <MotionMaybe
                            enable={shouldAnimFeatures}
                            nodeKey={`${remountKey}-left-grid`}
                            motionProps={containerMotionProps}
                        >
                            <ul
                                className={`grid pt-10 ${sideColumns} justify-items-center gap-8 lg:justify-items-start lg:gap-10 ${
                                    isRightAligned ? 'lg:order-1' : ''
                                }`}
                            >
                                {renderedPeople.map(({ Person }) => {
                                    const { x: imagePosX, y: imagePosY } =
                                        parseImagePosition(
                                            Person.ImagePosition
                                        );
                                    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;
                                    return (
                                        <MotionMaybe
                                            key={Person.Name}
                                            enable={shouldAnimFeatures}
                                            nodeKey={`${remountKey}-left-person-${Person.Name}`}
                                            motionProps={childMotionProps}
                                            className={
                                                isLeftAligned || isRightAligned
                                                    ? 'text-center sm:text-left'
                                                    : 'text-center'
                                            }
                                        >
                                            <div
                                                className={`flex flex-col ${
                                                    isLeftAligned ||
                                                    isRightAligned
                                                        ? 'items-center sm:items-start'
                                                        : 'items-center'
                                                }`}
                                            >
                                                <div className="relative">
                                                    <img
                                                        className={`${sideImageClass} rounded-[var(--shape-radius)] object-cover`}
                                                        src={Person.ImageURL}
                                                        alt={Person.Name}
                                                        loading="lazy"
                                                        style={{
                                                            objectPosition:
                                                                imageObjectPosition,
                                                        }}
                                                    />
                                                </div>
                                                <MarkDown
                                                    variant={
                                                        personHeadingVariant
                                                    }
                                                    className={`mt-6 font-display text-sm leading-4 tracking-tight md:text-base ${taglineColor} ${
                                                        isLeftAligned ||
                                                        isRightAligned
                                                            ? 'text-center sm:text-left'
                                                            : 'text-center'
                                                    }`}
                                                >
                                                    {Person.Name}
                                                </MarkDown>
                                                <MarkDown
                                                    className={`mt-2 text-xs font-text leading-4 md:text-sm ${textColor} ${
                                                        isLeftAligned ||
                                                        isRightAligned
                                                            ? 'text-center sm:text-left'
                                                            : 'text-center'
                                                    }`}
                                                >
                                                    {Person.Role}
                                                </MarkDown>
                                                {renderSocialLinks(
                                                    Person,
                                                    isLeftAligned ||
                                                        isRightAligned
                                                        ? 'justify-center sm:justify-start'
                                                        : 'justify-center'
                                                )}
                                                {Person.Description && (
                                                    <MarkDown
                                                        className={`mt-2 text-xs font-text leading-4 ${textColor} text-center sm:text-left ${singlePersonDescriptionWidth} ${
                                                            hasSinglePerson
                                                                ? 'mx-auto sm:mx-0'
                                                                : ''
                                                        }`}
                                                    >
                                                        {Person.Description}
                                                    </MarkDown>
                                                )}
                                            </div>
                                        </MotionMaybe>
                                    );
                                })}
                            </ul>
                        </MotionMaybe>
                    </div>
                )}
            </div>
        </section>
    );
}
